import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';

const OrderNow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const p = location.state?.product;

  const selectedOptions = p?.selectedOptions || {};
  const selectedOptionItems = p?.selectedOptionItems || [];
  const optionPrice = Number(p?.optionPrice || 0);

  const selectedAddressFromState = location.state?.selectedAddress;
  const selectedCouponIdFromState = location.state?.selectedCouponId;

  const [orderPreview, setOrderPreview] = useState(null);

  const [buyer, setBuyer] = useState(() => ({
    name: '',
    phone: ''
  }));
  
  const [editingBuyer, setEditingBuyer] = useState(false);
  const phoneInputRef = useRef(null);

  const [selectedAddress, setSelectedAddress] = useState(null);

  const [orderType, setOrderType] = useState('pickup');
  const [reserveYn, setReserveYn] = useState('N');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [pickupDatetime, setPickupDatetime] = useState('');
  const [showMiniInfo, setShowMiniInfo] = useState(false);

  const [coupons, setCoupons] = useState([]);
  const [selectedCoupon, setSelectedCoupon] = useState('');

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const selectedCouponObj = useMemo(() => {
    return coupons.find(c => String(c.member_coupon_id) === selectedCoupon);
  }, [coupons, selectedCoupon]);

  const optionTotalPrice = useMemo(() => {
    return optionPrice * Number(orderPreview?.quantity || p?.quantity || 1);
  }, [optionPrice, orderPreview, p]);
  
  const displaySubtotal = useMemo(() => {
    if (!orderPreview) return 0;
    return Number(orderPreview.subtotal || 0) + optionTotalPrice;
  }, [orderPreview, optionTotalPrice]);

  const couponDiscount = useMemo(() => {
    if (!selectedCouponObj || !orderPreview) return 0;
  
    const subtotal = Number(displaySubtotal || 0);
  
    // 최소 주문금액 체크
    if (subtotal < selectedCouponObj.min_order_price) return 0;
  
    if (selectedCouponObj.discount_type === 'amount') {
      return selectedCouponObj.discount_value;
    }
  
    if (selectedCouponObj.discount_type === 'percent') {
      return Math.floor(subtotal * (selectedCouponObj.discount_value / 100));
    }
  
    return 0;
  }, [selectedCouponObj, displaySubtotal]);

  useEffect(() => {
    if (!p) {
      alert('상품 정보가 없습니다.');
      navigate(-1);
      return;
    }

    if (!window.daum) {
      const script = document.createElement('script');
      script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
      script.async = true;
      document.body.appendChild(script);
    }

    const qs = new URLSearchParams(location.search);
    const reserveYnQ = qs.get('reserve_yn') || 'N';
    const pickupDateQ = qs.get('pickup_date') || '';
    const pickupTimeQ = qs.get('pickup_time') || '';
    const pickupDatetimeQ = qs.get('pickup_datetime') || '';

    axios
.get(
      `/api/order/now/preview?product_id=${p.id}&quantity=${p.quantity}`
      + `&reserve_yn=${encodeURIComponent(reserveYnQ)}`
      + `&pickup_date=${encodeURIComponent(pickupDateQ)}`
      + `&pickup_time=${encodeURIComponent(pickupTimeQ)}`
      + `&pickup_datetime=${encodeURIComponent(pickupDatetimeQ)}`
          )
      .then((res) => {
        const d = res.data;
        setOrderPreview(d);

        if (selectedAddressFromState) {
          setSelectedAddress(selectedAddressFromState);
        } else {
          const def =
            d.default_address ||
            d.all_addresses?.find(a => Number(a.is_default) === 1) ||
            d.all_addresses?.[0] ||
            null;
        
          setSelectedAddress(def);
        }

        setBuyer(prev => ({
          name: d.member_info?.name || d.member_info?.nickname || '',
          phone: prev.phone || d.member_info?.phone || ''
        }));

        setReserveYn(d.reserve_yn_q || 'N');
        setPickupDate(d.pickup_date_q || '');
        setPickupTime(d.pickup_time_q || '');
        setPickupDatetime(d.pickup_datetime_q || '');

        const orderTypeFromState = location.state?.orderType;

        if (orderTypeFromState) {
          setOrderType(orderTypeFromState);
        } else {
          setOrderType('pickup');
        }
      })
      .catch((err) => {
        if (err.response && err.response.data && err.response.data.message) {
          alert(err.response.data.message);
        } else {
          alert('데이터를 불러올 수 없습니다.');
        }
        navigate(-1);
      });
  }, [p, navigate, location.search]);

  useEffect(() => {
    if (phoneInputRef.current && editingBuyer) {
      phoneInputRef.current.focus();
    }
  }, [editingBuyer]);

  useEffect(() => {
      if (selectedCouponIdFromState) {
          setSelectedCoupon(String(selectedCouponIdFromState));
      } else {
          setSelectedCoupon('');
      }
  }, [selectedCouponIdFromState]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    axios.get('/api/coupon', {
        withCredentials: true
    })
    .then(res => {
      // res.data.success가 없더라도 쿠폰 데이터가 있으면 처리하도록 방어코드 작성
      if (res.data.success || res.data.coupons) {
        setCoupons(res.data.coupons || []);

        // 쿠폰 페이지에서 전달받은 ID가 있다면 선택 처리 (정확한 변수명 사용)
        if (selectedCouponIdFromState) {
          setSelectedCoupon(String(selectedCouponIdFromState));
        }
      }
    })
    .catch(err => {
        console.error(err);
    });
  }, [selectedCouponIdFromState]); // 의존성 배열에 추가하여 상태 유동성 확보

  const displayDeliveryCost = useMemo(() => {
    if (!orderPreview) return 0;
    if (orderType !== 'delivery') return 0;
    return Number(displaySubtotal || 0) >= 30000 ? 0 : 3000;
  }, [orderPreview, orderType, displaySubtotal]);

  const displayTotalPrice = useMemo(() => {
    const delivery = Number(displayDeliveryCost || 0);
    return Math.max(0, displaySubtotal + delivery - couponDiscount);
  }, [displaySubtotal, displayDeliveryCost, couponDiscount]);

  const productDiscount = useMemo(() => {
    if (!orderPreview) return 0;
  
    const price = Number(orderPreview.product.price || 0);
    const display = Number(orderPreview.product.display_price || 0);
  
    // 할인 없으면 0
    if (display >= price) return 0;
  
    return (price - display) * orderPreview.quantity;
  }, [orderPreview]);

  const handleChangeToDelivery = () => {
    setOrderType('delivery');
  };

  const handleChangeToPickup = () => {
    setOrderType('pickup');
  };

  const handleReserveToggle = (e) => {
    const checked = e.target.checked;

    if (!orderPreview) return;

    if (checked) {
      setReserveYn('Y');

      const params = new URLSearchParams();
      params.set('product_id', orderPreview.product_id || orderPreview.product?.id);
      params.set('quantity', orderPreview.quantity);
      params.set('reserve_yn', 'Y');
      params.set('selected_coupon_id', selectedCoupon);

      if (pickupDate) params.set('pickup_date', pickupDate);
      if (pickupTime) params.set('pickup_time', pickupTime);
      if (pickupDatetime) params.set('pickup_datetime', pickupDatetime);

      navigate(`/pickup/calendar?${params.toString()}`, {
        state: {
          ...location.state,
          product: p,
          orderType,
          selectedCouponId: selectedCoupon
        }
      });
      
    } else {
      setReserveYn('N');
      setPickupDate('');
      setPickupTime('');
      setPickupDatetime('');

      const params = new URLSearchParams();
      params.set('product_id', orderPreview.product_id || orderPreview.product?.id);
      params.set('quantity', orderPreview.quantity);

      navigate(
        {
          pathname: location.pathname,
          search: `?${params.toString()}`
        },
        {
          replace: true,
          state: location.state
        }
      );
    }
  };

  const formatPhoneNumber = (value) => {
    const onlyNums = value.replace(/\D/g, '');
  
    if (onlyNums.length < 4) return onlyNums;
    if (onlyNums.length < 8) {
      return onlyNums.replace(/(\d{3})(\d+)/, '$1-$2');
    }
    return onlyNums.replace(/(\d{3})(\d{4})(\d+)/, '$1-$2-$3');
  };

  const formatPhoneDisplay = (phone = '') => {
    let nums = phone.replace(/\D/g, '');
  
    // 010 없이 들어온 짧은 번호는 앞에 010 보정
    if (nums.length <= 9) {
      nums = '010' + nums;
    }
  
    // 11자리 기준
    if (nums.length >= 11) {
      nums = nums.slice(0, 11);
      return nums.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    }
  
    return nums;
  };

  const handleBuyerButton = async () => {

    // 처음 클릭은 입력창만 열기
    if (!editingBuyer) {
      setEditingBuyer(true);
      return;
    }
  
    if (!buyer.phone.trim()) {
      alert('전화번호를 입력해주세요.');
      phoneInputRef.current?.focus();
      return;
    }
  
    try {
      await axios.post('/api/order/buyer', {
        buyer: {
          phone: buyer.phone.replace(/\D/g, '')
        }
      });
  
      setEditingBuyer(false);
  
    } catch (err) {
      alert(err.response?.data?.message || '저장에 실패했습니다.');
    }
  };

  const handlePayment = async () => {
    if (!buyer.name || !buyer.phone) {
      alert('주문자 정보를 입력해주세요.');
  
      setEditingBuyer(true);
  
      setTimeout(() => {
        phoneInputRef.current?.focus();
      }, 100);
  
      return;
    }
  
    if (orderType === 'delivery') {
      if (!selectedAddress) {
        alert('배송지를 선택해주세요.');
        return;
      }
    }
  
    if (orderType === 'pickup' && reserveYn === 'Y') {
      if (!pickupDate || !pickupTime || !pickupDatetime) {
        alert('예약픽업을 선택하셨으면 날짜와 시간을 먼저 선택해 주세요.');
        return;
      }
    }
  
    try {
  
      // 서버로 보낼 데이터
      const requestData = {
        product_id: orderPreview.product.id,
        quantity: orderPreview.quantity,
      
        selected_options: selectedOptions,
        selected_option_items: selectedOptionItems,
        option_price: optionPrice,
        option_total_price: optionTotalPrice,
      
        member_coupon_id: selectedCoupon || null,
      
        order_type: orderType,
        reserve_yn: reserveYn,
        pickup_date: pickupDate,
        pickup_time: pickupTime,
        pickup_datetime: pickupDatetime,
      
        buyer
      };
  
      // 배달일 때만 receiver 추가
      if (orderType === 'delivery') {
        requestData.receiver = {
          name: selectedAddress.receiver_name,
          phone: selectedAddress.receiver_phone,
          address: selectedAddress.address,
          detail: selectedAddress.detail_address
        };
      }
  
      const res = await axios.post('/api/order/now', requestData);
  
      if (res.data.order_id) {
        navigate('/pay/now', {
          state: {
            orderId: res.data.order_id,
            amount: res.data.amount || displayTotalPrice,
            orderName: orderPreview.product.name,
            buyerName: buyer.name,
            reserve_yn: reserveYn,
            pickup_date: pickupDate,
            pickup_time: pickupTime,
            pickup_datetime: pickupDatetime
          }
        });
      }
  
    } catch (err) {
      if (err.response?.data?.message) {
        alert(err.response.data.message);
      } else {
        alert('주문 처리 중 오류가 발생했습니다.');
      }
    }
  };

  if (!orderPreview) return <div style={{ padding: '50px', textAlign: 'center' }}>로딩 중...</div>;

  return (
    <div
      style={{
        ...styles.bodyWrapper,
        paddingBottom: isMobile ? '90px' : '40px'
      }}
    >
      <div style={styles.container}>
        <h2 style={styles.pageTitle}>결제하기</h2>

        <div style={styles.orderDetailCard}>
          <div style={styles.orderHeader}><strong>주문내역</strong></div>
          <div style={styles.itemRow}>
            <img
              src={
                orderPreview.product.image_path?.startsWith('http')
                  ? orderPreview.product.image_path
                  : `/static/${orderPreview.product.image_path}`
              }
              alt="상품 이미지"
              style={styles.itemImg}
            />

            {(() => {
              const qty = Number(orderPreview.quantity || 0);
              const basePrice = Number(orderPreview.product.display_price || 0);
              const originPrice = Number(orderPreview.product.price || 0);
              const optionTotal = Number(optionPrice || 0);

              const unitPrice = basePrice + optionTotal;
              const originUnitPrice = originPrice + optionTotal;
              const hasOption = selectedOptionItems.length > 0;
              const isSale = originPrice !== basePrice;

              return (
                <>
                  <div style={{ flexGrow: 1 }}>
                    <div style={styles.itemName}>
                      {orderPreview.product.name}
                    </div>

                    {hasOption && (
                      <div style={styles.optionBox}>
                        {selectedOptionItems.map(opt => (
                          <div key={`${opt.group_id}-${opt.item_id}`} style={styles.optionText}>
                            {opt.group_name}: {opt.item_name}
                            {Number(opt.extra_price || 0) > 0 &&
                              ` (+${Number(opt.extra_price || 0).toLocaleString()}원)`
                            }
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={styles.itemSub}>
                      수량: {qty}
                    </div>

                    {isSale && (
                      <div
                        style={{
                          textDecoration: 'line-through',
                          color: '#999',
                          fontSize: '12px',
                          marginTop: '3px'
                        }}
                      >
                        {Number(originUnitPrice * qty).toLocaleString()}원
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      ...styles.itemPrice,
                      marginTop: hasOption ? '0px' : '10px'
                    }}
                  >
                    {Number(unitPrice * qty).toLocaleString()}원
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        <div
          style={{
            margin: '0 15px',
            borderBottom: '1px solid #e5e5e5'
          }}
        />

        {/* 쿠폰 */}
        <div style={styles.orderDetailCard}>
          <div style={styles.orderHeader}>
            <strong>쿠폰</strong>
          </div>

          <div
            onClick={() =>
              navigate('/coupon', {
                state: {
                  returnTo: '/order/now',
                  product: p,
                  orderType,
                  selectedCoupon: selectedCouponObj,
                  // ★ 현재 주문 소계 금액을 함께 넘겨줍니다.
                  subtotal: Number(orderPreview?.subtotal || 0),

                  reserveYn,
                  pickupDate,
                  pickupTime,
                  pickupDatetime
                }
              })
            }
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px',
              cursor: 'pointer'
            }}
          >
            <div>
              <div
                style={{
                  color: '#888',
                  fontSize: '14px',
                  marginTop: '-18px'
                }}
              >
                {selectedCouponObj
                  ? `${selectedCouponObj.coupon_name} - ${couponDiscount.toLocaleString()}원 할인 적용됨`
                  : '사용 가능한 쿠폰 선택'}
              </div>
            </div>

            <div style={{ color: '#999', fontSize: '22px', marginTop: '-20px' }}>
              ›
            </div>
          </div>
        </div>

        <div
          style={{
            margin: '0 15px',
            borderBottom: '1px solid #e5e5e5'
          }}
        />

        <div style={styles.orderDetailCard}>
          <div style={styles.orderHeader}>
            <strong>주문자 정보</strong>

            <button
              type="button"
              onClick={handleBuyerButton}
              style={{
                border: '1px solid #ddd',
                background: '#fff',
                borderRadius: '20px',
                padding: '2px 14px',
                cursor: 'pointer',
                fontSize: '16px',
                marginRight: '10px'
              }}
            >
              {editingBuyer ? '저장' : '변경'}
            </button>
          </div>

          <div style={{ padding: '10px' }}>

            {/* 이름은 항상 표시 */}
            <div
              style={{
                fontSize: '18px',
                fontWeight: '700',
                marginBottom: '4px'
              }}
            >
              {buyer.name}
            </div>

            {/* 전화번호 */}
            {editingBuyer ? (
              <input
                ref={phoneInputRef}
                autoFocus
                value={buyer.phone}
                maxLength={13}
                onChange={(e) =>
                  setBuyer({
                    ...buyer,
                    phone: formatPhoneNumber(e.target.value)
                  })
                }
                placeholder="전화번호 입력"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '2px solid #0073e9',
                  borderRadius: '6px',
                  outline: 'none',
                  backgroundColor: '#fff',
                  color: '#333',
                  fontSize: '16px'
                }}
              />
            ) : buyer.phone ? (
              <div
                style={{
                  color: '#555'
                }}
              >
                {formatPhoneDisplay(buyer.phone)}
              </div>
            ) : (
              <div
                onClick={() => setEditingBuyer(true)}
                style={{
                  color: '#0d6efd',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                + 전화번호 입력
              </div>
            )}

            
              <div
                style={{
                  marginTop: '10px',
                  fontSize: '13px',
                  color: '#888'
                }}
              >
                주문 관련 안내를 받을 연락처입니다.
              </div>
           
          </div>
        </div>

        <div
          style={{
            margin: '0 15px',
            borderBottom: '1px solid #e5e5e5'
          }}
        />

        {Number(orderPreview.delivery_enabled) === 1 && (
          <div style={styles.orderDetailCard}>
            <div style={styles.orderHeader}>
              <strong>이용방법</strong>
            </div>

            <div style={styles.orderTypeToggle}>
              <button
                type="button"
                onClick={handleChangeToPickup}
                style={
                  orderType === 'pickup'
                    ? styles.orderTypeBtnActive
                    : styles.orderTypeBtn
                }
              >
                매장 픽업
              </button>

              <button
                type="button"
                onClick={handleChangeToDelivery}
                style={
                  orderType === 'delivery'
                    ? styles.orderTypeBtnActive
                    : styles.orderTypeBtn
                }
              >
                배달 수령
              </button>
            </div>
          </div>
        )}

        <div
          style={{
            margin: '0 15px',
            borderBottom: '1px solid #e5e5e5'
          }}
        />

        {orderType === 'delivery' &&
        Number(orderPreview.delivery_enabled) === 1 && (

          <div style={styles.orderDetailCard}>
            <div style={styles.orderHeader}>
              <strong>배송정보</strong>

              <button
                type="button"
                onClick={() =>
                  navigate('/address/list', {
                    state: {
                      returnTo: '/order/now',
                      product: p,
                      orderType,
                      selectedCouponId: selectedCoupon
                    }
                  })
                }
                style={{
                  border: '1px solid #ddd',
                  background: '#fff',
                  borderRadius: '20px',
                  padding: '2px 14px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  marginRight: '10px'
                }}
              >
                변경
              </button>
            </div>

            <div style={{ padding: '10px' }}>
              {selectedAddress ? (
                <>
                  <div
                    style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      marginBottom: '10px'
                    }}
                  >
                    {selectedAddress.receiver_name}
                  </div>

                  <div
                    style={{
                      marginBottom: '10px',
                      lineHeight: '1.5'
                    }}
                  >
                    {selectedAddress.address}
                    {' '}
                    {selectedAddress.detail_address}
                  </div>

                  <div
                    style={{
                      marginBottom: '10px',
                      color: '#555'
                    }}
                  >
                    {formatPhoneDisplay(selectedAddress.receiver_phone)}
                  </div>
                </>
              ) : (
                <>
                  <div
                    style={{
                      textAlign: 'center',
                      padding: '20px',
                      color: '#999'
                    }}
                  >
                    등록된 배송지가 없습니다.
                  </div>

                  <div
                    style={{
                      textAlign: 'center',
                      marginTop: '16px'
                    }}
                  >
                    <span
                      onClick={() =>
                        navigate('/address/list', {
                          state: {
                            returnTo: '/order/now',
                            product: p,
                            orderType,
                            selectedCouponId: selectedCoupon
                          }
                        })
                      }
                      style={{
                        fontWeight: '700',
                        color: '#0073e9',
                        cursor: 'pointer'
                      }}
                    >
                      + 배송지 등록
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {orderType === 'pickup' && (
          <div style={styles.orderDetailCard}>
            <div style={{ ...styles.orderHeader, position: 'relative' }}>
              <div>
                <strong>픽업시간</strong>
                <span
                  onClick={() => setShowMiniInfo(!showMiniInfo)}
                  style={styles.infoIcon}
                >
                  <span style={{ position: 'relative', top: '0px' }}>i</span>
                </span>
              </div>

              {showMiniInfo && (
                <div style={styles.miniInfoBox}>
                  <strong style={{ color: '#222' }}>매장 픽업 안내</strong>
                  <hr style={{ margin: '8px 0' }} />
                  • 매장픽업은 배송료없음<br />
                  • 체크박스해제(바로픽업)는 주문즉시 준비해드립니다.<br />
                  • 체크박스선택(예약픽업)는 예약한 시간까지 준비해드립니다.
                  <hr style={{ margin: '8px 0' }} />
                </div>
              )}
            </div>

            <label style={styles.reserveLabel}>
              <input
                type="checkbox"
                checked={reserveYn === 'Y' && !!pickupDatetime}
                onChange={handleReserveToggle}
                style={styles.reserveCheckbox}
              />
              예약하고 찾으러 갈게요!
            </label>

            {!!pickupDatetime && (
              <div style={styles.pickupSelectedBox}>
                {String(pickupDatetime).slice(0, 16).replace('T', ' ')}까지 방문할게요!
              </div>
            )}
          </div>
        )}

        {/* 결제금액 */}
        <div
          style={{
            ...styles.orderDetailCard,
            padding: 0
          }}
        >
          <div style={styles.priceSummary}>
            <div style={styles.priceRow}>
              <span>상품 금액</span>
              <span>
                {(Number(orderPreview.subtotal || 0) + productDiscount).toLocaleString()}원
              </span>
            </div>

            <div style={styles.priceRow}>
              <span>상품 할인</span>
              <span>-{productDiscount.toLocaleString()}원</span>
            </div>

            {optionTotalPrice > 0 && (
              <div style={styles.priceRow}>
                <span>옵션 금액</span>
                <span>{optionTotalPrice.toLocaleString()}원</span>
              </div>
            )}

            <div style={styles.priceRow}>
              <span>쿠폰 할인</span>
              <span>-{couponDiscount.toLocaleString()}원</span>
            </div>

            <div style={styles.priceRow}>
              <span>배송료</span>
              <span>{Number(displayDeliveryCost).toLocaleString()}원</span>
            </div>

            <hr
              style={{
                margin: '12px 0',
                border: '0',
                borderTop: '1px solid #eee'
              }}
            />

            <div
              style={{
                ...styles.priceRow,
                fontWeight: '700',
                fontSize: '18px'
              }}
            >
              <span>최종 결제금액</span>
              <span style={{ color: '#0073e9' }}>
                {Number(displayTotalPrice).toLocaleString()}원
              </span>
            </div>
          </div>
        </div>

        {isMobile ? (
          <div style={styles.bottomFixed}>
            <button
              style={styles.btnBuyMobile}
              onClick={handlePayment}
            >
              {orderType === 'delivery'
                ? '배달주문 결제하기'
                : pickupDatetime
                  ? '예약픽업 결제하기'
                  : '바로픽업 결제하기'}
            </button>
          </div>
        ) : (
          <div style={styles.bottomAction}>
            <button
              style={styles.submitBtn}
              onClick={handlePayment}
            >
              {orderType === 'delivery'
                ? '배달주문 결제하기'
                : pickupDatetime
                  ? '예약픽업 결제하기'
                  : '바로픽업 결제하기'}
            </button>
          </div>
        )}

              </div>
            </div>
          );
        };

const styles = {
  bodyWrapper: {
    backgroundColor: '#fff',
    minHeight: '100vh',
    paddingBottom: '40px'
  },
  container: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '0 6px',
    boxSizing: 'border-box',
    fontFamily: "'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif"
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    padding: '24px 16px 0',
    margin: '0'
  },
  orderDetailCard: {
    background: '#fff',
    marginTop: '0px',
    borderRadius: '1px',
    padding: '7px'
  },
  orderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: '0px solid #eee',
    paddingBottom: '6px',
    marginTop: '10px',
    marginBottom: '6px',
    fontSize: '18px',
    paddingLeft: '10px'
  },
  orderTypeToggle: {
    display: 'flex',
    margin: '20px 10px 20px 10px',
    gap: '10px',
    padding: '0px 0px'
  },
  orderTypeBtn: {
    flex: 1,
    padding: '12px',
    border: '1px solid #ddd',
    background: '#fff',
    cursor: 'pointer',
    borderRadius: '6px',
    fontWeight: 'bold'
  },
  orderTypeBtnActive: {
    flex: 1,
    padding: '12px',
    border: '2px solid #0d6efd',
    background: '#fff',
    cursor: 'pointer',
    borderRadius: '6px',
    fontWeight: 'bold'
  },
  cardBody: { padding: '10px' },
  itemRow: { display: 'flex', alignItems: 'center', gap: '15px', padding: '10px 15px' },
  itemImg: {
    width: '60px',
    height: '60px',
    objectFit: 'cover',
    border: '0px solid #ddd',
    borderRadius: '4px',
    marginLeft: '-5px'   // ← 핵심
  },
  itemName: { fontWeight: 'bold', fontSize: '16px' },
  itemSub: { color: '#6c757d', fontSize: '12px' },
  itemPrice: { textAlign: 'right', fontWeight: '600', fontSize: '16px', alignSelf: 'flex-start', marginTop: '10px' },
  priceSummary: {
    margin: '0',
    marginLeft: '-6px',
    marginRight: '-6px',
    padding: '20px',
    backgroundColor: '#f8f8f8',
    fontSize: '14px'
  },
  priceRow: { display: 'flex', justifyContent: 'space-between' },
  tabMenu: {
    justifyContent: 'center',
    display: 'flex',
    listStyle: 'none',
    padding: '0',
    margin: '0px 10px 0px 10px',
    borderTop: '2px solid #e9ecef',
    borderBottom: '2px solid #e9ecef',
    fontWeight: '600'
  },
  tab: {
    padding: '15px 20px 12px',
    cursor: 'pointer',
    color: '#6c757d',
    fontSize: '11px',
    lineHeight: '1.2',
    borderBottom: '3px solid transparent',
    transition: 'all 0.3s ease',
    flex: 1,
    textAlign: 'center'
  },
  tabActive: {
    padding: '15px 20px 12px',
    cursor: 'pointer',
    color: '#0073e9',
    fontSize: '11px',
    lineHeight: '1.2',
    borderBottom: '3px solid #0073e9',
    transition: 'all 0.3s ease',
    fontWeight: '700',
    flex: 1,
    textAlign: 'center'
  },
  floatingGroup: { position: 'relative', marginBottom: '1rem' },
  input: {
    width: '100%',
    padding: '1.5rem 0.75rem 0.5rem',
    fontSize: '1rem',
    border: '1px solid #dee2e6',
    borderRadius: '0.375rem',
    boxSizing: 'border-box',
    outline: 'none'
  },
  floatingLabel: {
    position: 'absolute',
    top: '0',
    left: '0',
    padding: '1rem 0.75rem',
    fontSize: '0.85rem',
    color: '#6c757d',
    pointerEvents: 'none',
    transform: 'scale(.85) translateY(-0.5rem) translateX(0.15rem)',
    transition: 'all 0.1s'
  },
  checkRow: { display: 'flex', alignItems: 'center', margin: '10px 0' },
  checkbox: { marginRight: '10px', width: '16px', height: '16px' },
  smallLabel: { fontSize: '0.875rem', color: '#444' },
  addrListItem: {
    display: 'flex',
    padding: '12px 0',
    borderBottom: '1px solid #e0e0e0',
    cursor: 'pointer',
    alignItems: 'flex-start'
  },
  addBtn: {
    padding: '10px 14px',
    borderRadius: '4px',
    cursor: 'pointer'
  },
  pickupSubmitWrap: {
    margin: '15px 10px 10px 10px'
  },
  submitBtn: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#0073e9',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontWeight: 'bold',
    fontSize: '1rem',
    cursor: 'pointer',
    margin: '10px 0 0 0',
    boxSizing: 'border-box'
  },
  infoIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    border: '1px solid #0d6efd',
    color: '#0d6efd',
    fontSize: '12px',
    marginLeft: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    position: 'relative',
    top: '-3px'
  },
  miniInfoBox: {
    display: 'block',
    position: 'absolute',
    top: '28px',
    left: '0',
    zIndex: 1055,
    width: '250px',
    fontSize: '0.75rem',
    color: '#6c757d',
    border: '1px solid #ddd',
    borderRadius: '8px',
    padding: '10px',
    background: '#f8f9fa',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
  },
  reserveLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '12px',
    marginBottom: '12px',
    fontSize: '16px',
    width: 'auto',
    cursor: 'pointer'
  },
  reserveCheckbox: {
    width: '18px',
    margin: '0px 0px 0px 10px',
    height: '18px'
  },
  pickupSelectedBox: {
    padding: '12px 14px',
    background: '#f3f3f3',
    borderRadius: '3px',
    color: '#2f5fd7',
    fontWeight: '700',
    margin: '0px 10px 20px 10px'
  },
  bottomAction: {
    margin: '20px 15px 30px'
  },
  bottomFixed: {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '58px',
    padding: '5px 15px 0 15px',
    display: 'flex',
    gap: '16px',
    zIndex: 1000,
    background: '#fff',
    borderTop: '0px solid #eee'
  },
  btnBuyMobile: {
    width: '100%',
    background: '#007bff',
    border: 'none',
    color: '#fff',
    borderRadius: '30px',
    height: '50px',
    fontSize: '15px',
    fontWeight: 500,
    cursor: 'pointer'
  },
  optionBox: {
    marginTop: '4px'
  },
  
  optionText: {
    color: '#777',
    fontSize: '12px',
    lineHeight: '1.4'
  },
};

export default OrderNow;
